/* ============================================================
   orderTimelineModel — TASK-248: pure stage model behind the
   shared role-aware order status timeline (extracted verbatim
   from OrderTimeline.tsx so the derivation runs under
   `node --test`, matching customerOrderView/providerOrderView).
   Composes Flowwow-like stages (placed → accepted → printing →
   result → confirmed → shipped → completed) with real timestamps
   from existing API data, role-specific next actions, and
   graceful terminal/empty states. No fake data: a stage shows a
   timestamp only when the API actually has one.

   TASK-248 delta — delivery-method-aware handoff language: the
   waiting/handoff copy after the customer confirmed the result
   is courier-biased («ждём отправку», stage «Отправлен»). With
   the G8-D1 DeliveryMethod snapshot the timeline now says what
   actually happens for самовывоз/ПВЗ orders: the item is handed
   over for pickup, not dispatched. Absent/legacy snapshot keeps
   the courier wording — zero behavior change for old rows.
   ============================================================ */

import type { WebDeliveryMethod, WebOrderStatus } from '../types/product';
import { isPickupDeliveryMethod } from './shippedTrackingView.ts';

export type TimelineRole = 'customer' | 'provider';

/** Everything the timeline needs — every optional field degrades gracefully. */
export interface OrderTimelineInput {
  status: WebOrderStatus;
  createdAtUtc: string;
  /** When the customer confirmed the printed result (gates shipping). */
  customerConfirmedAtUtc?: string | null;
  completedAtUtc?: string | null;
  /** When the dispute was opened (currently only on the customer detail DTO). */
  disputedAtUtc?: string | null;
  /** Earliest result-photo timestamp = when the result was submitted. */
  resultSubmittedAtUtc?: string | null;
  /** TASK-248: G8-D1 delivery-method snapshot; absent/legacy = Courier copy. */
  deliveryMethod?: WebDeliveryMethod | number | null;
}

export interface TimelineStage {
  key: 'placed' | 'accepted' | 'printing' | 'result' | 'confirmed' | 'shipped' | 'completed';
  label: string;
  state: 'done' | 'current' | 'upcoming';
  /** Real timestamp when the API has one, null otherwise. */
  timestampUtc: string | null;
}

export interface OrderTimelineModel {
  stages: TimelineStage[];
  /** Role-specific hint for the current stage, null when nothing to do. */
  nextAction: string | null;
  /** Set for terminal flows that replace the stepper. */
  terminal: { kind: 'cancelled' | 'disputed'; title: string; detail: string } | null;
  /** 0..6 index of the current stage, -1 for terminal flows. */
  currentIndex: number;
}

const STAGE_LABELS: Record<TimelineStage['key'], string> = {
  placed: 'Оформлен',
  accepted: 'Принят исполнителем',
  printing: 'Печать',
  result: 'Результат готов',
  confirmed: 'Подтверждён клиентом',
  shipped: 'Отправлен',
  completed: 'Завершён',
};

/** TASK-248: pickup orders end in a handover for collection, not dispatch. */
const STAGE_LABEL_SHIPPED_PICKUP = 'Готов к выдаче';

const NEXT_ACTION: Record<TimelineRole, Partial<Record<WebOrderStatus, string>>> = {
  customer: {
    Pending: 'Ждём ответа исполнителя — можно обсудить условия во встречном предложении',
    Accepted: 'Исполнитель готовится к печати',
    Printing: 'Заказ печатается',
    Printed: 'Проверьте фото результата и подтвердите',
    Shipped: 'Заказ в пути — проверьте его при получении',
    Completed: 'Можно оставить отзыв на странице исполнителя',
  },
  provider: {
    Pending: 'Принять заказ или предложить свои условия',
    Accepted: 'Можно начинать печать',
    Printing: 'Прикрепите фото готового результата',
    Printed: 'Ждём подтверждения клиента',
    Shipped: 'Заказ доставляется',
    Completed: 'Работа завершена',
  },
};

/** TASK-248: method-aware Shipped copy — the collection point replaces the
 *  courier-in-transit phrasing for самовывоз/ПВЗ orders. */
const NEXT_ACTION_SHIPPED_PICKUP: Record<TimelineRole, string> = {
  customer: 'Заказ можно получить — проверьте его при выдаче',
  provider: 'Заказ ждёт получения клиентом',
};

/** Index of the furthest stage the order has really reached. */
function progressIndex(input: OrderTimelineInput): number {
  const confirmed = input.customerConfirmedAtUtc != null;
  switch (input.status) {
    case 'Pending':
      return 0;
    case 'Accepted':
      return 1;
    case 'Printing':
      return 2;
    case 'Printed':
      return confirmed ? 4 : 3;
    case 'Shipped':
      return 5;
    case 'Completed':
      return 6;
    // Dispute freezes progress at the point it was opened (Printed or after
    // confirmation); the terminal banner carries the full context.
    case 'Disputed':
      return confirmed ? 4 : 3;
    case 'Cancelled':
      return 0;
  }
}

/** Pure stage model — trivially unit-testable and shared by both renderers. */
export function buildOrderTimeline(input: OrderTimelineInput, role: TimelineRole): OrderTimelineModel {
  if (input.status === 'Cancelled') {
    return {
      stages: [],
      nextAction: null,
      currentIndex: -1,
      terminal: {
        kind: 'cancelled',
        title: 'Заказ отменён',
        detail: 'Оформите новый заказ, если печать всё ещё нужна.',
      },
    };
  }

  if (input.status === 'Disputed') {
    const when = input.disputedAtUtc
      ? new Date(input.disputedAtUtc).toLocaleString('ru-RU')
      : null;
    return {
      stages: [],
      nextAction: null,
      currentIndex: -1,
      terminal: {
        kind: 'disputed',
        title: 'Открыт спор',
        detail:
          (when ? `${when} — ` : '') +
          'Поддержка изучит ситуацию и вернётся с решением.',
      },
    };
  }

  const reached = progressIndex(input);
  const pickup = isPickupDeliveryMethod(input.deliveryMethod);
  const timestamps: Record<TimelineStage['key'], string | null> = {
    placed: input.createdAtUtc ?? null,
    accepted: null, // not stored today — the stage still renders, just undated
    printing: null,
    result: input.resultSubmittedAtUtc ?? null,
    confirmed: input.customerConfirmedAtUtc ?? null,
    shipped: null, // not stored today
    completed: input.completedAtUtc ?? null,
  };

  const stages: TimelineStage[] = (Object.keys(STAGE_LABELS) as TimelineStage['key'][]).map((key, i) => ({
    key,
    label: key === 'shipped' && pickup ? STAGE_LABEL_SHIPPED_PICKUP : STAGE_LABELS[key],
    state: i < reached ? 'done' : i === reached ? 'current' : 'upcoming',
    timestampUtc: timestamps[key],
  }));

  // Confirmed-but-waiting-for-shipping is its own actionable state.
  // TASK-248: the handoff verb follows the delivery method — pickup orders
  // are prepared for collection, courier orders are dispatched.
  const nextAction =
    input.status === 'Printed' && input.customerConfirmedAtUtc != null
      ? role === 'provider'
        ? pickup
          ? 'Передайте заказ клиенту на выдаче'
          : 'Отправьте заказ и укажите трек-номер'
        : pickup
          ? 'Результат подтверждён — ждём готовности к выдаче'
          : 'Результат подтверждён — ждём отправку'
      : input.status === 'Shipped' && pickup
        ? NEXT_ACTION_SHIPPED_PICKUP[role]
        : NEXT_ACTION[role][input.status] ?? null;

  return { stages, nextAction, terminal: null, currentIndex: reached };
}
